import { RowOrigin } from '../models/source-key.model'
import { MatchingService } from '../services/matching.service'

/**
 * What an Unmatch click should actually do, resolved before any UI is opened.
 *
 *  - `none`    — the scope is not matched; the click is a no-op (the menu item is disabled in this
 *                state anyway, so this is the belt-and-braces branch).
 *  - `dissolve`— the group has exactly one other origin, so there is nothing to choose: detach the
 *                whole group.
 *  - `choose`  — the group spans two or more other origins, so the user picks which one to detach
 *                (or the whole group) in the Unmatch modal.
 *
 * Split out of the grid component so the dissolve-vs-modal branch — an acceptance criterion of
 * PVAL-26403 — is unit-testable without standing up NgbModal.
 */
export type UnmatchPlan =
  | { kind: 'none' }
  | { kind: 'dissolve'; groups: string[] }
  | { kind: 'choose'; groups: string[]; origins: RowOrigin[] }

/**
 * Decide the Unmatch branch for a scope of rows.
 *
 * "Other origins" comes from {@link MatchingService.otherOriginsFor}, which counts the CONCRETE
 * feeds (Custody and Trading are two origins, not one Prometheus) — so a Custody+Trading+Enfusion
 * group opens the modal with two choices from an Enfusion row's menu, and with one from a Custody
 * row's. That is the intended asymmetry: the question the modal asks is "detach which of the
 * sources you are matched TO".
 */
export function planUnmatch(
  matching: MatchingService,
  rowIds: string[],
): UnmatchPlan {
  const groups = matching.groupsFor(rowIds)
  if (!groups.length) return { kind: 'none' }
  const origins = matching.otherOriginsFor(rowIds)
  if (origins.length <= 1) return { kind: 'dissolve', groups }
  return { kind: 'choose', groups, origins }
}
